import { ArrowRightCircleIcon, PlusIcon, ArrowPathIcon, BarsArrowUpIcon, BarsArrowDownIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import api from '../../../middleware/api'
import { toast } from 'react-toastify'
import { useAuth } from '../../../context/AuthContext'
import CapsuleOverview from './CapsuleOverview'
import SelectionActions from './SelectionActions'
import Loading from '../../Common/Loading/Loading'
import LoadingSpinner from '../../Common/Loading/LoadingSpinner'

const CapsulesList = () => {
    const { capsules, setData, customer } = useOutletContext()
    const { user } = useAuth()
    const navigate = useNavigate()

    const [selectedCapsules, setSelectedCapsules] = useState([]);
    const [expandedStates, setExpandedStates] = useState({});
    const [isCreating, setIsCreating] = useState(false);
    const [isAllExpanded, setIsAllExpanded] = useState(false);
    const handleNewCapsule = async () => {
        if (isCreating) return;
        setIsCreating(true);
        try {
            const response = await api.post('/capsules/new', { userId: user.id, customerId: customer.id })
            if (response.status === 201 && response.data.id) {
                const path = window.location.pathname.includes('capsule') ? '.' : './capsule'
                setData((prev) => ({
                    ...prev,
                    capsules: [...prev.capsules, response.data]
                }))
                navigate(`${path}/${response.data.id}`)
            } else {
                toast.error('Erreur lors de la création de la capsule. \n Veuillez réessayer dans quelques instants')
            }
        } catch (error) {
            toast.error('Erreur lors de la création de la capsule. \n Veuillez réessayer dans quelques instants')
        } finally {
            setIsCreating(false);
        }
    }

    const handleDeleteCapsule = async (capsuleId) => {
        try {
            const response = await api.delete(`/capsules/delete/${capsuleId}`)
            if (response.status === 200) {
                setData((prev) => {
                    return {
                        ...prev,
                        capsules: prev.capsules.filter(capsule => capsule.id !== capsuleId)
                    }
                })
                toast.success('Capsule supprimée avec succès')
            }
        } catch (e) {
            toast.error('Erreur lors de la suppression de la capsule. \n Veuillez réessayer dans quelques instants')
        }
    }

    const duplicateCapsule = async (capsuleId, duplicateAllConfigurations) => {
        try {
            const response = await api.post(`/capsules/duplicate/${capsuleId}`, { duplicateAllConfigurations })
            if (response.status === 201 && response.data.id) {
                const path = window.location.pathname.includes('capsule') ? '.' : 'capsule'
                setData((prev) => {
                    return {
                        ...prev,
                        capsules: [...prev.capsules, response.data]
                    }
                })
                navigate(`${path}/${response.data.id}`)
            } else {
                toast.error('Erreur lors de la duplication de la capsule. \n Veuillez réessayer dans quelques instants')
            }
        } catch (e) {
            toast.error('Erreur lors de la duplication de la capsule. \n Veuillez réessayer dans quelques instants')
        }
    }

    const handleCheckboxChange = (capsuleId) => {
        setSelectedCapsules((prevSelected) => {
            if (prevSelected.includes(capsuleId)) {
                return prevSelected.filter(id => id !== capsuleId);
            } else {
                return [...prevSelected, capsuleId];
            }
        });
    };

    const expandAllCapsules = () => {
        const newStates = {};
        capsules.forEach(capsule => {
            newStates[capsule.id] = true;
        });
        setExpandedStates(newStates);
    };

    const collapseAllCapsules = () => {
        const newStates = {};
        capsules.forEach(capsule => {
            newStates[capsule.id] = false;
        });
        setExpandedStates(newStates);
    };

    const toggleAccordion = (capsuleId) => {
        setExpandedStates(prevStates => ({
            ...prevStates,
            [capsuleId]: !prevStates[capsuleId]
        }));
    };

    const toggleAllCapsules = () => {
        setIsAllExpanded(!isAllExpanded);
        if (!isAllExpanded) {
            expandAllCapsules();
        } else {
            collapseAllCapsules();
        }
    };

    const getLastOrder = (capsule) => {
        const orders = customer?.customerOrders?.filter(order => order.relatedCapsuleIds.includes(capsule.id))
        return orders.sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate))[0]
    }

    if (capsules.length === 0) return (
        <div className='w-full h-full flex justify-center items-center'>
            <button
                onClick={handleNewCapsule}
                disabled={isCreating}
                className='group cursor-pointer flex flex-col p-4 mt-36 items-center gap-y-4 hover:bg-primary transition-all rounded-lg hover:scale-90 disabled:opacity-50 disabled:cursor-not-allowed'
            >
                {isCreating ? (
                    <LoadingSpinner style="medium" />
                ) : (
                    <PlusIcon className="w-16 h-16 text-gray-300 group-hover:text-white" />
                )}
                <span className='text-md text-gray-300 text-center group-hover:text-white group-hover:font-semibold'>
                    {isCreating ? 'Création en cours...' : 'Nouveau Bilan'}
                </span>
            </button>
        </div>
    )

    return (
        <div className="relative z-0 w-full mt-12 flex gap-4 flex-col pb-8">
            <div className="relative flex flex-col items-center gap-2 ">
                <h3>Echelles des Sensibilités</h3>
                <div className='flex flex-row gap-2'>
                    <p>[<span className='font-bold'>1</span>] Sensible</p>
                    <p>[<span className='font-bold'>2</span>] Sensible ++</p>
                    <p>[<span className='font-bold'>3</span>] Intolérant</p>
                    <p>[<span className='font-bold'>4</span>] Allergique</p>
                    <p>[<span className='font-bold'>5</span>] Anaphylactique</p>
                </div>
                <div className="absolute top-4 right-0 flex justify-end mb-4 gap-2 w-fit flex-wrap">
                    <button
                        onClick={handleNewCapsule}
                        disabled={isCreating}
                        className="btn btn-sm btn-primary flex items-center gap-2"
                    >
                        {isCreating ? (
                            <LoadingSpinner style="small" />
                        ) : (
                            <PlusIcon className="w-5 h-5" />
                        )}
                        {isCreating ? 'Création...' : 'Nouveau Bilan'}
                    </button>
                    <button
                        onClick={toggleAllCapsules}
                        className="btn btn-sm rounded-full btn-primary flex items-center gap-2"
                    >
                        {isAllExpanded ? <BarsArrowUpIcon className="w-6 h-6" /> : <BarsArrowDownIcon className="w-6 h-6" />}
                    </button>
                </div>
            </div>
            {selectedCapsules.length > 0 && <SelectionActions
                selectedCapsules={selectedCapsules}
                setSelectedCapsules={setSelectedCapsules}
                setData={setData}
                capsules={capsules}
                customer={customer}
            />}
            <div className='w-full 2xl:w-11/12 m-auto flex flex-col gap-8'>
                {capsules
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(capsule =>
                        <CapsuleOverview
                            key={capsule.id}
                            capsule={capsule}
                            deleteCapsule={handleDeleteCapsule}
                            duplicateCapsule={duplicateCapsule}
                            selectedCapsules={selectedCapsules}
                            handleCheckboxChange={handleCheckboxChange}
                            isExpanded={expandedStates[capsule.id] || false}
                            toggleAccordion={() => toggleAccordion(capsule.id)}
                            orders={getLastOrder(capsule)}
                        />
                    )}
            </div>
        </div>
    )
}

export default CapsulesList
